import type { EntityDetail } from '../api/client.js';

export interface PlayDataEnvelope {
  action: 'play';
  data: Record<string, unknown>;
}

export const PLAYER_IGNORE_REQUESTS = ['saveResponse', 'notifyOnPreview'];
const ALLOWED_IMAGE_SCHEMES = /^https?:\/\//i;
const ALLOWED_IMAGE_HOSTS = /(?:^|\.)((amazonaws\.com)|(cloudfront\.net))$/i;

export function serializeContentUnit(entity: EntityDetail): PlayDataEnvelope {
  const playerContent = normalizePlayerContent(entity);

  return {
    action: 'play',
    data: {
      id: entity.id,
      type: 'entity',
      item_type_slug: entity.item_type_slug,
      title: entity.title,
      summary: processHtml(entity.summary ?? ''),
      rc_text: processHtml(entity.rc_text ?? ''),
      options: entity.options ?? {},
      steps: playerContent.steps.map(({ step, syntheticCatchallId, forceTryAgain }) =>
        serializeStep(
          step,
          playerContent.responseTypes,
          syntheticCatchallId,
          forceTryAgain,
        ),
      ),
      responses: playerContent.responses.map((response) => serializeResponse(response)),
      _revisionHash: entity._revisionHash,
    },
  };
}

function serializeStep(
  step: EntityDetail['steps'][number],
  responseTypes: ReadonlyMap<number, string>,
  syntheticCatchallId: number | null,
  forceTryAgain: boolean,
) {
  const normalizedResponseFormat = normalizeResponseFormat(step.response_format);
  const responseGroups = getResponseGroups(step, normalizedResponseFormat, responseTypes);
  const stepLevelResponseIds = getStepLevelResponseIds(
    step,
    responseTypes,
    syntheticCatchallId,
  );
  const isQuestion = Boolean(
    step.is_question ??
      (step.response_format !== 'content' && responseGroups.length > 0),
  );

  return {
    ...serializeEmbeddedObject(step as unknown as Record<string, unknown>),
    // The player's terminal-step check requires next_step_id to be explicitly null
    // (strict ===); corpus packets omit the field on repair-feedback steps, which
    // silently disables the learner-mode "Try again" flow. Normalize the omission
    // at the embed boundary like we do for options.
    next_step_id: step.next_step_id ?? null,
    is_question: isQuestion,
    is_try_again: forceTryAgain || Boolean(step.is_try_again),
    response_format: normalizedResponseFormat,
    response_groups: responseGroups,
    response_ids: stepLevelResponseIds,
    options: serializeEmbeddedObject(step.options ?? {}),
    video_url: step.video_url ?? null,
    video_text: processHtml(step.video_text ?? ''),
  };
}

function serializeResponse(response: EntityDetail['responses'][number]) {
  return {
    ...serializeEmbeddedObject(response as unknown as Record<string, unknown>),
    alt_values: response.alt_values ?? [],
    type: response.type ?? 'regular',
  };
}

function synthesizeResponseGroups(stepId: number, responseIds: number[]) {
  if (!responseIds.length) {
    return [];
  }

  return [
    {
      id: `step-${stepId}-group-0`,
      options: {},
      response_ids: responseIds,
    },
  ];
}

function getResponseGroups(
  step: EntityDetail['steps'][number],
  normalizedResponseFormat: string,
  responseTypes: ReadonlyMap<number, string>,
) {
  if (step.response_groups && step.response_groups.length > 0) {
    return step.response_groups.map((group) => ({
      ...serializeEmbeddedObject(group as unknown as Record<string, unknown>),
      // The editable player reads responseGroup.options.type without guarding
      // options. Brain2 content legitimately omits the object, so normalize it
      // at the embed boundary just as we do for steps and synthesized groups.
      options: serializeEmbeddedObject(
        ((group as unknown as { options?: Record<string, unknown> }).options ?? {}),
      ),
    }));
  }

  if (step.response_format === 'content') {
    // Brain2/player model explanation steps as single_choice with an empty response group.
    return [createEmptyResponseGroup(step.id)];
  }

  const responseIds = (step.response_ids ?? []).filter(
    (responseId) => responseTypes.get(responseId) !== 'catchall',
  );
  if (normalizedResponseFormat === 'single_choice' && responseIds.length === 0) {
    return [createEmptyResponseGroup(step.id)];
  }

  return synthesizeResponseGroups(step.id, responseIds);
}

function getStepLevelResponseIds(
  step: EntityDetail['steps'][number],
  responseTypes: ReadonlyMap<number, string>,
  syntheticCatchallId: number | null,
) {
  const existingStepLevelIds = step.response_groups && step.response_groups.length > 0
    ? (step.response_ids ?? [])
    : (step.response_ids ?? []).filter(
        (responseId) => responseTypes.get(responseId) === 'catchall',
      );

  return syntheticCatchallId == null
    ? existingStepLevelIds
    : [...existingStepLevelIds, syntheticCatchallId];
}

// Authored packets may contain only the accepted values for a numeric question,
// while Player 2.1.40 requires an ungrouped catch-all response to resolve every
// submitted value. Supply a preview-only repair branch when that catch-all is
// absent; saved lesson content remains unchanged.
function normalizePlayerContent(entity: EntityDetail) {
  const responses = [...(entity.responses ?? [])];
  const responseTypes = new Map(
    responses.map((response) => [response.id, response.type ?? 'regular']),
  );
  let nextResponseId = responses.reduce(
    (highest, response) => Math.max(highest, response.id),
    entity.response_counter ?? 0,
  ) + 1;
  let nextStepId = (entity.steps ?? []).reduce(
    (highest, step) => Math.max(highest, step.id),
    entity.step_counter ?? 0,
  ) + 1;
  const syntheticFeedbackSteps: Array<{
    step: EntityDetail['steps'][number];
    syntheticCatchallId: null;
    forceTryAgain: false;
  }> = [];

  const steps = (entity.steps ?? []).map((step) => {
    if (!needsSyntheticNumericCatchall(step, responseTypes)) {
      return { step, syntheticCatchallId: null, forceTryAgain: false };
    }

    const syntheticCatchallId = nextResponseId;
    nextResponseId += 1;
    const syntheticFeedbackStepId = nextStepId;
    nextStepId += 1;
    responses.push({
      id: syntheticCatchallId,
      value: 'Other responses',
      is_correct: false,
      next_step_id: syntheticFeedbackStepId,
      type: 'catchall',
      alt_values: [],
    });
    responseTypes.set(syntheticCatchallId, 'catchall');
    syntheticFeedbackSteps.push({
      step: {
        id: syntheticFeedbackStepId,
        prompt: 'Not quite. Enter another value.',
        next_step_id: null,
        is_root: false,
        is_question: false,
        is_try_again: false,
        response_format: 'content',
        response_ids: [],
        response_groups: [],
        options: { isShownWithoutResponses: true },
      },
      syntheticCatchallId: null,
      forceTryAgain: false,
    });

    return { step, syntheticCatchallId, forceTryAgain: true };
  });

  return {
    steps: [...steps, ...syntheticFeedbackSteps],
    responses,
    responseTypes,
  };
}

function needsSyntheticNumericCatchall(
  step: EntityDetail['steps'][number],
  responseTypes: ReadonlyMap<number, string>,
) {
  if (step.response_format !== 'numeric') {
    return false;
  }

  const groupedResponseIds = (step.response_groups ?? []).flatMap(
    (group) => group.response_ids,
  );
  const allResponseIds = [...groupedResponseIds, ...(step.response_ids ?? [])];
  const hasAnswer = allResponseIds.some(
    (responseId) => responseTypes.get(responseId) !== 'catchall',
  );
  const hasCatchall = allResponseIds.some(
    (responseId) => responseTypes.get(responseId) === 'catchall',
  );

  return hasAnswer && !hasCatchall;
}

function createEmptyResponseGroup(stepId: number) {
  return {
    id: `step-${stepId}-group-0`,
    options: {},
    response_ids: [],
  };
}

function serializeEmbeddedObject(value: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(value).map(([key, fieldValue]) => [
      key,
      typeof fieldValue === 'string' ? processHtml(fieldValue) : fieldValue,
    ]),
  );
}

function normalizeResponseFormat(responseFormat: string | undefined) {
  if (responseFormat === 'multiple_choice' || responseFormat === 'content') {
    return 'single_choice';
  }

  return responseFormat ?? 'single_choice';
}

function processHtml(value: string) {
  return value.replace(/\[img:([^\]]+)\]/g, (_match, rawUrl: string) => {
    const trimmed = rawUrl.trim();
    if (!ALLOWED_IMAGE_SCHEMES.test(trimmed)) {
      return '[invalid image]';
    }

    try {
      const parsed = new URL(trimmed);
      if (!ALLOWED_IMAGE_HOSTS.test(parsed.hostname)) {
        return '[invalid image host]';
      }

      const escaped = trimmed
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      return `<img src="${escaped}" alt="" />`;
    } catch {
      return '[invalid image]';
    }
  });
}
